"use client"

import React, { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { Send, LogOut, Settings, AlertCircle, Menu, Search, X, ChevronRight, User, ChevronLeft, Trash2, Share, MoreVertical, ChevronDown, Edit, Sparkles } from 'lucide-react'
import { useAuth } from '../../contexts/auth-context'
import { useSubscription } from '../../contexts/subscription-context'
import { functions, db } from '../../lib/firebase'
import { httpsCallable } from 'firebase/functions'
import { doc, onSnapshot, collection, query, where, orderBy } from 'firebase/firestore'
import { DebateStatus, MessageRole, LLM_CONFIG, LLM_FAMILIES, LLM_MODELS, getAvailableModels } from '../../types/debate'
import ShareModal from './ShareModal'
import DeleteConfirmationModal from './DeleteConfirmationModal'
import RenameModal from './RenameModal'
import { trackDebateStart, trackDebateShare, trackDebateDelete } from '../../lib/analytics'

// Add this helper function after the imports and before the component
const isPendingStatus = (status) => {
  return ['pending', 'in_progress'].includes(status);
};

// Update the canUseModel helper function
const canUseModel = (modelConfig, subscription) => {
  if (!modelConfig) return false;
  
  // If model doesn't specify any subscription requirements, it's available to all
  if (!modelConfig.requiresPro && !modelConfig.requiresUltimate) {
    return true;
  }
  
  // Free tier can only use models that don't require pro or ultimate
  if (!subscription) {
    return false;
  }
  
  // Pro tier can use free and pro models, but not ultimate models
  if (subscription.tier === 'pro') {
    return !modelConfig.requiresUltimate;
  }
  
  // Ultimate tier can use all models
  if (subscription.tier === 'ultimate') {
    return true;
  }
  
  return false;
};

// Create a new component for the message content
const MessageContent = ({ content, shouldStream }) => {
  const [displayedText, setDisplayedText] = useState('');

  useEffect(() => {
    if (!shouldStream) {
      setDisplayedText(content);
      return;
    }

    // Split by spaces while preserving newlines
    const words = content.split(/(\s+)/);
    let currentWordIndex = 0;

    const interval = setInterval(() => {
      if (currentWordIndex < words.length) {
        // Get a random number of words between 1 and 13 (or remaining words if less)
        const remainingWords = words.length - currentWordIndex;
        const wordsToAdd = Math.min(Math.floor(Math.random() * 13) + 1, remainingWords);
        
        // Add the next batch of words, preserving all whitespace
        const newWords = words.slice(0, currentWordIndex + wordsToAdd).join('');
        setDisplayedText(newWords);
        currentWordIndex += wordsToAdd;
      } else {
        clearInterval(interval);
      }
    }, 90); // Higher slows it down, lower speeds it up

    return () => clearInterval(interval);
  }, [content, shouldStream]);

  return <p className="whitespace-pre-wrap">{displayedText}</p>;
};

// Component for the LLM model selector
const ModelSelector = ({ selectedModel, onModelChange, subscription, disabled }) => {
  const [isOpen, setIsOpen] = useState(false);
  const availableModels = getAvailableModels();

  const handleModelSelect = (model) => {
    const modelConfig = LLM_CONFIG[model];
    if (model !== selectedModel && canUseModel(modelConfig, subscription)) {
      onModelChange(model);
    }
    setIsOpen(false);
  };

  // Group models by family
  const groupedModels = Object.entries(availableModels).reduce((acc, [modelId, config]) => {
    const family = config.family;
    if (!acc[family]) {
      acc[family] = [];
    }
    acc[family].push([modelId, config]);
    return acc;
  }, {});

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm border ${
          disabled 
            ? 'border-gray-700 text-gray-500 cursor-not-allowed' 
            : 'border-gray-700 text-gray-300 hover:bg-gray-800 hover:border-gray-600'
        }`}
        disabled={disabled}
      >
        <span>{LLM_CONFIG[selectedModel]?.name || 'Select model'}</span>
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {isOpen && (
        <div className="absolute left-0 bottom-full mb-1 w-60 bg-gray-800 rounded-lg shadow-lg py-1 z-50">
          {Object.entries(groupedModels).map(([family, models]) => (
            <div key={family} className="py-1">
              <div className="px-4 pt-1 pb-1 text-xs text-gray-500 uppercase">
                {family === LLM_FAMILIES.GPT ? 'OpenAI Models' : 
                 family === LLM_FAMILIES.CLAUDE ? 'Claude Models' : 
                 family === LLM_FAMILIES.GEMINI ? 'Gemini Models' : 'Other Models'}
              </div>
              <div className="border-b border-gray-700 mb-1"></div>
              
              {models.map(([modelId, config]) => {
                const isAvailable = canUseModel(config, subscription);
                const subscriptionRequired = config.requiresUltimate ? 'Ultimate' : (config.requiresPro ? 'Pro' : null);
                
                return (
                  <button
                    key={modelId}
                    type="button"
                    onClick={() => handleModelSelect(modelId)}
                    className={`w-full px-4 py-2 text-left flex items-center justify-between ${
                      modelId === selectedModel
                        ? 'bg-purple-500/20 text-purple-300'
                        : isAvailable
                        ? 'text-gray-300 hover:bg-gray-700'
                        : 'text-gray-500 cursor-not-allowed'
                    }`}
                    disabled={!isAvailable}
                  >
                    <span className="flex-1 text-sm">{config.name}</span>
                    {subscriptionRequired && !isAvailable && (
                      <span className="text-xs text-purple-400 ml-2">{subscriptionRequired}</span>
                    )}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default function ChatInterface() {
  const navigate = useNavigate()
  const { user, logout } = useAuth()
  const { remainingChats, hasActiveSubscription, canMakeMoreChats, subscription } = useSubscription()
  const [messages, setMessages] = useState([])
  const [input, setInput] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [debateId, setDebateId] = useState(null)
  const [debates, setDebates] = useState([])
  const [searchTerm, setSearchTerm] = useState('')
  const [isSidebarOpen, setIsSidebarOpen] = useState(true)
  const messagesEndRef = useRef(null)
  const [showWelcomeMessage, setShowWelcomeMessage] = useState(true)
  const [hasError, setHasError] = useState(false);
  const debatesListenerRef = useRef(null);
  const [shareModalOpen, setShareModalOpen] = useState(false)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [debateToDelete, setDebateToDelete] = useState(null)
  const [shareUrl, setShareUrl] = useState('')
  const [menuOpenDebateId, setMenuOpenDebateId] = useState(null)
  const [loadingActions, setLoadingActions] = useState({ share: null, delete: null });
  const menuRef = useRef(null);
  const [processingStatus, setProcessingStatus] = useState('');
  const [streamedMessageIds, setStreamedMessageIds] = useState(new Set());
  const debateListenerRef = useRef(null);  // Add this ref for the individual debate listener
  const [isFollowUpMode, setIsFollowUpMode] = useState(false);
  const [selectedModel, setSelectedModel] = useState(LLM_MODELS.GPT_4O_MINI);
  const [currentDebateLLM, setCurrentDebateLLM] = useState(null);
  const [renameModalOpen, setRenameModalOpen] = useState(false)
  const [debateToRename, setDebateToRename] = useState(null)
  const [renameLoading, setRenameLoading] = useState(false)

  // Current helper functions for subscription tiers
  const isPro = () => hasActiveSubscription() && subscription?.tier === 'pro';
  const isUltimate = () => hasActiveSubscription() && subscription?.tier === 'ultimate';

  // Add click outside handler
  useEffect(() => {
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpenDebateId(null);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Load user's debates
  useEffect(() => {
    if (!user) return;

    const setupDebatesListener = () => {
      const q = query(
        collection(db, 'debates'),
        where('userId', '==', user.uid),
        orderBy('createdAt', 'desc')
      );

      try {
        const currentDebatesListener = debatesListenerRef.current;
        if (currentDebatesListener) {
          currentDebatesListener();
        }

        debatesListenerRef.current = onSnapshot(q, (snapshot) => {
          const debatesList = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          setDebates(debatesList);
          setHasError(false);
        }, (error) => {
          console.error('Error loading debates:', error);
          setHasError(true);
          // Try to reconnect after a delay
          setTimeout(setupDebatesListener, 5000);
        });
      } catch (error) {
        console.error('Error setting up debates listener:', error);
        setHasError(true);
        setTimeout(setupDebatesListener, 5000);
      }
    };

    setupDebatesListener();

    return () => {
      const currentDebatesListener = debatesListenerRef.current;
      if (currentDebatesListener) {
        currentDebatesListener();
      }
    };
  }, [user]);

  useEffect(() => {
    if (debateId) {
      const loadDebate = async () => {
        try {
          // If not in cache or not completed, subscribe to Firestore
          const q = doc(db, 'debates', debateId);
          const unsubscribe = onSnapshot(q, async (doc) => {
            const debateData = doc.data();
            if (debateData) {
              // Get the new messages
              const newMessages = debateData.uiMessages || [];
              
              // Set the current debate's specific model
              setCurrentDebateLLM(debateData.model || LLM_MODELS.GPT_4O);
              
              // Mark new messages for streaming if they haven't been seen before
              if (isPendingStatus(debateData.status)) {
                const lastMessageIndex = messages.length;
                const actualNewMessages = newMessages.slice(lastMessageIndex);
                
                if (actualNewMessages.length > 0) {
                  setStreamedMessageIds(prev => {
                    const updated = new Set(prev);
                    actualNewMessages.forEach((_, index) => {
                      updated.add(lastMessageIndex + index);
                    });
                    return updated;
                  });
                }
              }
              
              // Always update messages to get new content
              setMessages(newMessages);
              // Update loading state based on debate status
              setIsLoading(isPendingStatus(debateData.status));
              setProcessingStatus(debateData.processingStatus || '');
            }
          });

          // Store the unsubscribe function
          debateListenerRef.current = unsubscribe;
        } catch (error) {
          console.error('Error in loadDebate:', error);
          setError('Failed to load debate');
        }
      };

      loadDebate();
    } else {
      // Reset current debate LLM when no debate is selected
      setCurrentDebateLLM(null);
    }

    return () => {
      if (debateListenerRef.current) {
        debateListenerRef.current();
        debateListenerRef.current = null;
      }
    };
  }, [debateId, messages.length]);

  useEffect(() => {
    if (debateId || messages.length > 0) {
      setShowWelcomeMessage(false)
    } else {
      setShowWelcomeMessage(true)
    }
  }, [debateId, messages])

  // Clean up any active listeners and reset state when user logs out
  useEffect(() => {
    if (!user) {
      // Clear any active listeners
      const currentDebatesListener = debatesListenerRef.current;
      if (currentDebatesListener) {
        currentDebatesListener();
        debatesListenerRef.current = null;
      }

      // Clear individual debate listener
      if (debateListenerRef.current) {
        debateListenerRef.current();
        debateListenerRef.current = null;
      }
      
      // Reset all state
      setDebates([]);
      setMessages([]);
      setDebateId(null);
      setError('');
      setIsLoading(false);
      setInput('');
      setShowWelcomeMessage(true);
      setStreamedMessageIds(new Set());
      setMenuOpenDebateId(null);
      setLoadingActions({ share: null, delete: null });
    }
  }, [user]);

  // Update scrollToBottom function to ensure it captures all content
  const scrollToBottom = () => {
    setTimeout(() => {
      const mainContainer = messagesEndRef.current?.closest('main');
      if (mainContainer) {
        mainContainer.scrollTo({
          top: mainContainer.scrollHeight,
          behavior: 'smooth'
        });
      }
    }, 300);
  }

  useEffect(() => {
    // Scroll to bottom whenever messages change or when loading state changes
    scrollToBottom()
  }, [messages, isLoading])

  const startNewChat = () => {
    setMessages([]);
    setDebateId(null);
    setError('');
    setIsLoading(false);
    setInput('');
    setShowWelcomeMessage(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!input.trim() || isLoading) return

    if (!canMakeMoreChats()) {
      navigate('/plans')
      return
    }

    const prompt = input.trim()
    setInput('')
    setError('')

    try {
      if (isFollowUpMode && debateId) {
        setMessages(prev => [...prev]);
        setStreamedMessageIds(new Set()); // Reset streaming for new follow-up
        setIsLoading(true); // Enable loading state for follow-up

        // Handle follow-up question for existing debate
        const continueDebate = httpsCallable(functions, 'continueDebate')
        const result = await continueDebate({ 
          debateId, 
          followUpPrompt: prompt 
        })
        
        if (!result.data?.success) {
          throw new Error(result.data?.error || 'Failed to continue debate')
        }
      } else {
        setIsLoading(true);
        // Handle new debate creation with selected LLM
        const createDebate = httpsCallable(functions, 'createDebate')
        const result = await createDebate({ 
          prompt,
          model: selectedModel
        })
        
        if (!result.data?.success || !result.data.data?.debateId) {
          throw new Error(result.data?.error || 'Failed to create debate')
        }

        trackDebateStart(prompt)
        setDebateId(result.data.data.debateId)
        // Set current debate LLM to the selected model
        setCurrentDebateLLM(selectedModel);
      }
      setIsSidebarOpen(window.innerWidth >= 768)
    } catch (err) {
      setError('Failed to start debate. Please try again.')
      console.error('Debate error:', err)
      setIsLoading(false)
    }
  }

  const handleLogout = async () => {
    try {
      await logout()
      navigate('/')
    } catch (err) {
      console.error('Logout error:', err)
    }
  }

  const handleDebateClick = (id) => {
    const selectedDebate = debates.find(d => d.id === id);
    setDebateId(id);
    setError('');
    // Automatically enable follow-up mode for pro users on completed debates
    if (selectedDebate?.status === DebateStatus.COMPLETED && hasActiveSubscription()) {
      setIsFollowUpMode(true);
    } else {
      setIsFollowUpMode(false);
    }
    if (window.innerWidth < 768) {
      setIsSidebarOpen(false);
    }
  }

  const handleDeleteDebate = async (id) => {
    if (!id) return;
    
    try {
      setLoadingActions(prev => ({ ...prev, delete: id }));
      const deleteDebate = httpsCallable(functions, 'deleteDebate');
      await deleteDebate({ debateId: id });
      trackDebateDelete(id);
      
      if (id === debateId) {
        startNewChat();
      }
      setMenuOpenDebateId(null);
      setError('');
    } catch (err) {
      console.error('Delete debate error:', err);
      setError('Failed to delete debate. Please try again.');
    } finally {
      setLoadingActions(prev => ({ ...prev, delete: null }));
      setDeleteModalOpen(false);
      setDebateToDelete(null);
    }
  };

  const handleShare = async (e, debate) => {
    if (!debate || debate.status !== 'completed') {
      setError('Only completed debates can be shared')
      return
    }

    setLoadingActions(prev => ({ ...prev, share: debate.id }));
    try {
      const createSharedDebate = httpsCallable(functions, 'createSharedDebate')
      const result = await createSharedDebate({ debateId: debate.id })
      
      if (!result.data?.success) {
        throw new Error(result.data?.error || 'Failed to share debate')
      }

      const sharedUrl = `${window.location.origin}/chat?share=${result.data.data.sharedId}`
      setShareUrl(sharedUrl)
      setShareModalOpen(true)
      setMenuOpenDebateId(null)
      trackDebateShare(debate.id)
    } catch (err) {
      console.error('Share debate error:', err)
      setError('Failed to share debate. Please try again.')
    } finally {
      setLoadingActions(prev => ({ ...prev, share: null }));
    }
  }

  // Update handleRename function to reset the rename form state after completion
  const handleRename = async (newName) => {
    if (!debateToRename) return;
    
    try {
      setRenameLoading(true);
      const renameDebate = httpsCallable(functions, 'renameDebate');
      await renameDebate({ 
        debateId: debateToRename.id, 
        name: newName 
      });
      
      setError('');
      setMenuOpenDebateId(null);
    } catch (err) {
      console.error('Rename debate error:', err);
      setError('Failed to rename debate. Please try again.');
    } finally {
      setRenameLoading(false);
      setRenameModalOpen(false);
      setDebateToRename(null);
    }
  };

  const filteredDebates = debates.filter(debate => 
    debate?.prompts?.[0]?.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const getMessageStyle = (role) => {
    switch (role) {
      case MessageRole.USER:
        return 'mx-auto max-w-2xl bg-gradient-to-r from-purple-500 to-pink-600 text-white'
      case MessageRole.PROTAGONIST:
        return 'ml-auto max-w-3xl bg-gradient-to-r from-blue-500 to-cyan-600 text-white'
      case MessageRole.ANTAGONIST:
        return 'mr-auto max-w-3xl bg-gradient-to-r from-red-500 to-orange-600 text-white'
      case MessageRole.EVALUATOR:
        return 'mx-auto w-full max-w-2xl bg-gradient-to-r from-green-500 to-emerald-600 text-white'
      default:
        return 'mx-auto bg-gray-800/50 text-gray-200'
    }
  }

  const MessageIcon = ({ role }) => {
    switch (role) {
      case MessageRole.USER:
        return <div className="bg-purple-500 rounded-full p-2"><User className="w-4 h-4 text-white" /></div>
      case MessageRole.PROTAGONIST:
        return <img src="/protagonist.png" alt="Protagonist" className="w-8 h-8 rounded-full" />
      case MessageRole.ANTAGONIST:
        return <img src="/antagonist.png" alt="Antagonist" className="w-8 h-8 rounded-full" />
      default:
        return null
    }
  }

  const getInputPlaceholder = () => {
    if (!debateId) {
      return isLoading ? "Starting a new debate..." : "Enter a topic to debate...";
    }

    const currentDebate = debates.find(d => d.id === debateId);
    if (!currentDebate) return "Loading debate...";

    if (isPendingStatus(currentDebate.status)) {
      return currentDebate.processingStatus || "Debate is in progress...";
    }
    
    switch (currentDebate.status) {
      case DebateStatus.COMPLETED:
        return hasActiveSubscription() 
          ? isFollowUpMode 
            ? "Enter your follow-up question..."
            : "Ask a follow-up question to continue the debate" 
          : "Upgrade to Pro to ask follow-up questions and continue the debate";
      case DebateStatus.ERROR:
        return "This debate encountered an error - start a new one to continue";
      default:
        return "Loading debate status...";
    }
  };

  // Add helper for rendering follow-up UI
  const renderFollowUpOption = () => {
    if (!debateId) return null;
    
    const currentDebate = debates.find(d => d.id === debateId);
    if (!currentDebate || currentDebate.status !== DebateStatus.COMPLETED) return null;

    if (!hasActiveSubscription()) {
      return (
        <div className="flex justify-center mt-4 mb-2">
          <button
            onClick={() => navigate('/plans')}
            className="flex items-center gap-2 text-purple-400 hover:text-purple-300 transition-colors"
          >
            <span className="text-sm font-medium border border-purple-400/50 rounded px-1.5">Pro</span>
            <span>Upgrade to continue this debate</span>
          </button>
        </div>
      );
    }

    return (
      <div className="flex justify-center mt-4 mb-2">
        <div className="flex items-center gap-2 text-gray-400">
          <span className="text-sm font-medium border border-purple-400/50 rounded px-1.5 text-purple-400">Pro</span>
          <span>Type your follow-up question below to continue the debate</span>
        </div>
      </div>
    );
  };

  const renderMessage = (message, index) => {
    const shouldStream = streamedMessageIds.has(index);

    return (
      <motion.div
        key={`message-${message.role}-${index}`}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        className={`mb-6 flex ${
          message.role === MessageRole.EVALUATOR || message.role === MessageRole.USER
            ? 'justify-center w-full' 
            : message.role === MessageRole.ANTAGONIST 
              ? 'justify-start' 
              : 'justify-end'
        }`}
      >
        <div className={`flex items-end space-x-2 ${
          message.role === MessageRole.EVALUATOR ? 'w-full max-w-2xl' : ''
        }`}>
          {(message.role === MessageRole.ANTAGONIST || message.role === MessageRole.USER) && (
            <MessageIcon role={message.role} />
          )}
          <div
            className={`rounded-2xl px-6 py-4 ${getMessageStyle(message.role)} ${
              message.role === MessageRole.EVALUATOR ? 'text-center my-8' : ''
            }`}
          >
            {message.role === MessageRole.EVALUATOR && (
              <div className="font-semibold mb-2">Final Evaluation</div>
            )}
            <MessageContent content={message.content} shouldStream={shouldStream} />
          </div>
          {message.role === MessageRole.PROTAGONIST && (
            <MessageIcon role={message.role} />
          )}
        </div>
      </motion.div>
    );
  };

  // Add a helper for getting the disabled state of the model selector
  const isModelSelectorDisabled = () => {
    return isLoading || debateId !== null;
  };

  // Add helper to render subscription badge
  const renderSubscriptionBadge = () => {
    if (isUltimate()) {
      return (
        <span className="text-sm font-medium bg-gradient-to-r from-blue-400 via-violet-400 to-indigo-400 text-transparent bg-clip-text border border-indigo-400/50 rounded px-1.5 flex items-center gap-1">
          <Sparkles className="w-3 h-3 text-indigo-400" />
          Ultimate
        </span>
      );
    } else if (isPro()) {
      return (
        <span className="text-sm font-medium text-purple-400 border border-purple-400/50 rounded px-1.5">
          Pro
        </span>
      );
    }
    return null;
  };

  // Add helper for showing current LLM model info if a debate is active
  const renderCurrentModelInfo = () => {
    if (!currentDebateLLM || !debateId) return null;
    
    const modelConfig = LLM_CONFIG[currentDebateLLM] || {};
    
    return (
      <div className="flex items-center gap-1.5 text-xs text-gray-400">
        <span>Using {modelConfig.name || 'AI Model'}</span>
        {modelConfig.requiresUltimate && (
          <span className="text-xs font-medium bg-gradient-to-r from-blue-400 via-violet-400 to-indigo-400 text-transparent bg-clip-text border border-indigo-400/50 rounded px-1 flex items-center gap-0.5">
            <Sparkles className="w-3 h-3 text-indigo-400" />
            Ultimate
          </span>
        )}
        {modelConfig.requiresPro && !modelConfig.requiresUltimate && (
          <span className="text-xs font-medium text-purple-400 border border-purple-400/50 rounded px-1">Pro</span>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen max-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 flex overflow-hidden">
      {hasError && (
        <div className="fixed top-4 right-4 bg-red-500/10 border border-red-500/50 text-red-500 px-4 py-2 rounded-lg z-50">
          Connection error. Trying to reconnect...
        </div>
      )}

      {/* Top navigation bar */}
      <div className="fixed top-0 left-0 right-0 h-14 bg-gray-900 border-b border-gray-800 z-40 flex items-center px-4">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            className="p-2 text-gray-400 hover:text-white transition-colors md:hidden"
          >
            <Menu className="w-5 h-5" />
          </button>
          <button
            onClick={startNewChat}
            className="text-xl font-bold bg-gradient-to-r from-purple-400 to-pink-600 text-transparent bg-clip-text flex items-center gap-2"
          >
            <span>Verdyct</span>
            {renderSubscriptionBadge()}
          </button>
        </div>
        <div className="flex items-center space-x-4 ml-auto">
          {!hasActiveSubscription() ? (
            <button
              onClick={() => navigate('/plans')}
              className="text-sm px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-600 rounded-lg text-white hover:opacity-90 transition-opacity"
            >
              Upgrade ({remainingChats} left)
            </button>
          ) : (
            <button
              onClick={() => navigate('/plans')}
              className="text-sm px-4 py-2 border border-gray-700 rounded-lg text-gray-300 hover:bg-gray-800 transition-colors"
            >
              Plans
            </button>
          )}
          <button
            onClick={() => navigate('/settings')}
            className="p-2 text-gray-400 hover:text-white transition-colors"
          >
            <Settings className="w-5 h-5" />
          </button>
          <button
            onClick={handleLogout}
            className="p-2 text-gray-400 hover:text-white transition-colors"
          >
            <LogOut className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Sidebar */}
      <div 
        className={`fixed inset-y-0 left-0 transform ${
          isSidebarOpen ? 'translate-x-0 w-72' : '-translate-x-full md:translate-x-0 md:w-12'
        } md:relative transition-all duration-300 ease-in-out z-30
        bg-gray-900 border-r border-gray-800 flex flex-col pt-14`}
      >
        <div className={`flex flex-col h-full transition-opacity duration-300 ${isSidebarOpen ? 'opacity-100' : 'opacity-0 md:opacity-100'}`}>
          <div className="flex-none p-4 border-b border-gray-800 flex items-center justify-between">
            {isSidebarOpen ? (
              <>
                <button
                  onClick={startNewChat}
                  className="flex items-center space-x-2 text-white bg-gradient-to-r from-purple-500 to-pink-600 px-4 py-2 rounded-lg hover:opacity-90 transition-opacity flex-1 justify-center"
                >
                  <ChevronRight className="w-5 h-5" />
                  <span>New Debate</span>
                </button>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setIsSidebarOpen(false)}
                    className="md:hidden text-gray-400 hover:text-white"
                  >
                    <X className="w-5 h-5" />
                  </button>
                  <button 
                    onClick={() => setIsSidebarOpen(false)}
                    className="hidden md:block text-gray-400 hover:text-white p-1 rounded-lg hover:bg-gray-800/50"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                </div>
              </>
            ) : (
              <button 
                onClick={() => setIsSidebarOpen(true)}
                className="hidden md:block w-full text-gray-400 hover:text-white p-1 rounded-lg hover:bg-gray-800/50"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            )}
          </div>

          {isSidebarOpen && (
            <>
              <div className="flex-none p-4 border-b border-gray-800">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search debates..."
                    className="w-full bg-gray-800/50 text-white placeholder-gray-400 rounded-lg pl-10 pr-4 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>
              </div>

              <div className="flex-1 overflow-y-auto">
                <div className="divide-y divide-gray-800">
                  {filteredDebates.map((debate) => (
                    <div
                      key={debate.id}
                      onClick={() => handleDebateClick(debate.id)}
                      className={`w-full p-4 text-left hover:bg-gray-800/50 transition-colors ${
                        debate.id === debateId ? 'bg-gray-800/50' : ''
                      } group relative cursor-pointer`}
                    >
                      <div className="flex justify-between items-start gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="text-white text-sm truncate">{debate.displayName}</p>
                          <p className="text-gray-400 text-xs mt-1">
                            {new Date(debate.createdAt.seconds * 1000).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="relative" ref={menuRef}>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setMenuOpenDebateId(menuOpenDebateId === debate.id ? null : debate.id);
                            }}
                            className="p-1.5 text-gray-500 hover:text-white opacity-0 group-hover:opacity-100 transition-opacity rounded hover:bg-gray-800"
                            aria-label="More options"
                          >
                            <MoreVertical className="w-4 h-4" />
                          </button>
                          {menuOpenDebateId === debate.id && (
                            <div 
                              className="absolute right-0 mt-1 w-48 bg-gray-800 rounded-lg shadow-lg py-1 z-50"
                              onMouseDown={e => e.stopPropagation()}
                              onClick={e => e.stopPropagation()}
                            >
                              <div className="py-1">
                                <button
                                  type="button"
                                  onClick={() => {
                                    setDebateToRename({...debate}); // Create a fresh copy of the debate object
                                    setRenameModalOpen(true);
                                    setMenuOpenDebateId(null);
                                  }}
                                  className="w-full px-4 py-2 text-left text-sm text-gray-300 hover:bg-gray-700 flex items-center gap-2"
                                >
                                  <Edit className="w-4 h-4" />
                                  Rename
                                </button>
                              </div>
                              <div className="py-1">
                                <button
                                  type="button"
                                  onClick={() => {
                                    if (debate.status !== 'completed') {
                                      setError('Only completed debates can be shared');
                                      return;
                                    }
                                    setLoadingActions(prev => ({ ...prev, share: debate.id }));
                                    handleShare(null, debate);
                                  }}
                                  disabled={loadingActions.share === debate.id || debate.status !== 'completed'}
                                  className="w-full px-4 py-2 text-left text-sm text-gray-300 hover:bg-gray-700 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  <Share className="w-4 h-4" />
                                  {loadingActions.share === debate.id ? 'Sharing...' : 'Share'}
                                </button>
                              </div>
                              <div className="py-1">
                                <button
                                  type="button"
                                  onClick={() => {
                                    setDebateToDelete(debate.id);
                                    setDeleteModalOpen(true);
                                  }}
                                  disabled={loadingActions.delete === debate.id}
                                  className="w-full px-4 py-2 text-left text-sm text-red-400 hover:bg-gray-700 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  <Trash2 className="w-4 h-4" />
                                  {loadingActions.delete === debate.id ? 'Deleting...' : 'Delete'}
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Main chat area with its own scrollable container */}
      <div className={`flex-1 flex flex-col min-h-screen pt-14 ${!isSidebarOpen ? 'md:ml-12' : ''}`}>
        <main className="flex-1 overflow-y-auto overscroll-contain" style={{ height: 'calc(100vh - 120px)' }}>
          <div className="mx-auto px-4 py-2">
            {showWelcomeMessage ? (
              <div className="flex flex-col items-center justify-center min-h-[50vh] text-center px-4">
                <h1 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-purple-400 to-pink-600 text-transparent bg-clip-text mb-2 flex items-center gap-2">
                  Welcome to Verdyct
                  {renderSubscriptionBadge()}
                </h1>
                <p className="text-gray-400 mb-4 max-w-lg text-sm">
                  Enter a topic below to start a debate between two AI agents. Watch as they present arguments and counter-arguments, moderated by an AI moderator.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-w-2xl w-full">
                  <div className="p-2 bg-gray-800/50 rounded-lg">
                    <h3 className="text-blue-400 font-semibold mb-1 text-sm">Protagonist</h3>
                    <p className="text-gray-400 text-xs">Presents arguments in favor of the topic</p>
                  </div>
                  <div className="p-2 bg-gray-800/50 rounded-lg">
                    <h3 className="text-red-400 font-semibold mb-1 text-sm">Antagonist</h3>
                    <p className="text-gray-400 text-xs">Challenges with counter-arguments</p>
                  </div>
                </div>
              </div>
            ) : (
              <AnimatePresence mode="popLayout">
                {/* Current model info badge */}
                {renderCurrentModelInfo()}
                
                {messages.map((message, index) => renderMessage(message, index))}
                
                {isLoading && (
                  <motion.div
                    key="loading"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="flex justify-start mb-6"
                  >
                    <div className="bg-gray-800/50 rounded-2xl px-6 py-4">
                      <div className="flex flex-col items-center space-y-2">
                        <div className="flex items-center space-x-2">
                          <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                          <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                          <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                        </div>
                        {processingStatus && (
                          <div className="text-sm text-gray-400">{processingStatus}</div>
                        )}
                      </div>
                    </div>
                  </motion.div>
                )}
                
                {error && (
                  <motion.div
                    key="error"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="mb-6 flex items-center justify-center"
                  >
                    <div className="flex items-center space-x-2 text-red-500 bg-red-500/10 px-4 py-2 rounded-lg">
                      <AlertCircle className="w-5 h-5" />
                      <span>{error}</span>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            )}
            <div ref={messagesEndRef} />
            {renderFollowUpOption()}
          </div>
        </main>

        <footer className="flex-none border-t border-gray-800 bg-gray-900 p-2">
          <div className="max-w-4xl mx-auto">
            <form onSubmit={handleSubmit} className="flex space-x-2">
              {/* Show model selector for new debates only */}
              {showWelcomeMessage && (
                <div className="hidden sm:block">
                  <ModelSelector 
                    selectedModel={selectedModel}
                    onModelChange={setSelectedModel}
                    subscription={subscription}
                    disabled={isModelSelectorDisabled()}
                  />
                </div>
              )}
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={getInputPlaceholder()}
                className="flex-1 bg-gray-800/50 text-white placeholder-gray-400 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-75"
                disabled={isLoading || !canMakeMoreChats() || (!hasActiveSubscription() && debateId)}
              />
              <button
                type="submit"
                disabled={isLoading || !input.trim() || !canMakeMoreChats() || (!hasActiveSubscription() && debateId)}
                className="px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-600 text-white rounded-lg font-semibold hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                <Send className="w-5 h-5" />
                <span className="hidden sm:inline">
                  {!debateId ? 'Start Debate' : 'Send Follow-up'}
                </span>
              </button>
            </form>
          </div>
        </footer>
      </div>
      <ShareModal 
        isOpen={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
        shareUrl={shareUrl}
      />
      <DeleteConfirmationModal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setDebateToDelete(null);
        }}
        onConfirm={() => handleDeleteDebate(debateToDelete)}
        isLoading={loadingActions.delete === debateToDelete}
      />
      <RenameModal
        key={`rename-modal-${debateToRename?.id || 'none'}`}
        isOpen={renameModalOpen}
        onClose={() => {
          setRenameModalOpen(false);
          setDebateToRename(null);
        }}
        onRename={handleRename}
        currentName={debateToRename?.displayName || debateToRename?.prompts?.[0] || ''}
        isLoading={renameLoading}
      />
    </div>
  );
}

